<template>
  <v-card v-if="dueDiligenceExemption" id="due-diligence-exemption-form">
    <v-card-title class="primary white--text">
      <v-row class="pa-2" justify="space-between">
        <span class="headline">
          {{ title }}
        </span>
        <BaseDialogActions hideRefresh hideFullscreen />
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-text-field
      class="pt-4"
      v-model="dueDiligenceExemption.name"
      :rules="[rules.required]"
      :label="$t('exemptionName')"
      :placeholder="$t('exemptionNamePlaceholder')"
      color="black"
      :counter="maxNameLength"
      :maxlength="maxNameLength"
      ref="dueDiligenceExemptionName"/>
      <v-textarea
      class="pt-2"
      v-model="dueDiligenceExemption.description"
      :label="$t('exemptionDescription')"
      :placeholder="$t('exemptionDescriptionPlaceholder')"
      color="black"
      rows="3"
      auto-grow
      :counter="maxDescriptionLength"
      :maxlength="maxDescriptionLength"/>
      <p v-if="options.length > 0" class="text-subtitle-2 mb-0 mt-2">{{ $t('options') }}</p>
      <v-checkbox
      v-for="option in options"
      :key="option.model"
      v-model="dueDiligenceExemption[option.model]"
      color="primary"
      hide-details
      class="mt-2"
      :id="`due-diligence-exemption-${option.model}`">
        <template #label>
          <span class="d-flex align-center">
            {{ option.label }}
            <Icon
            icon="mdi-information-outline"
            iconColor="info"
            margin="ml-2"
            :tooltipText="option.info"/>
          </span>
        </template>
      </v-checkbox>

      <v-alert
      v-if="notes.length > 0"
      type="info"
      text
      outlined
      dense
      icon="mdi-information-outline"
      class="exemption-notes mt-4 mb-0">
        <div v-for="(note, index) in notes" :key="index" class="text-caption" :class="{ 'mt-1': index > 0 }">{{ note }}</div>
      </v-alert>
      <v-row no-gutters justify="end" class="mt-4">
        <v-btn id="due-diligence-exemption-submit" color="primary" @click="submitClicked" :disabled="!canSubmit">
          {{ buttonText }}
        </v-btn>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import fieldRules from '@/utils/rules.js'
import { StringConstants } from '@/utils/constants'
import { speciesLayerEnabled, dueDiligenceApprovalEnabled } from '@/utils/dueDiligence/mode.js'

export default {
  name: 'DueDiligenceExemptionForm',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    propDueDiligenceExemption: {
      type: Object,
      required: false,
      default: undefined
    }
  },

  created () {
    if (this.propDueDiligenceExemption) {
      this.dueDiligenceExemption = cloneDeep(this.propDueDiligenceExemption)
      this.dueDiligenceExemption.description = this.dueDiligenceExemption.description ?? ''
      this.dueDiligenceExemption.requiresApproval = !!this.dueDiligenceExemption.requiresApproval
      this.dueDiligenceExemption.autoAssignWhenNoHits = !!this.dueDiligenceExemption.autoAssignWhenNoHits
    }
  },

  mounted () {
    setTimeout(_ => {
      this.$refs.dueDiligenceExemptionName.focus()
    }, 0)
  },

  computed: {
    ...mapGetters('due-diligence-exemption', ['allDueDiligenceExemptions']),

    editing () {
      return this.dueDiligenceExemption?.dueDiligenceExemptionId
    },

    title () {
      return this.editing ? this.$t('editDueDiligenceExemption') : this.$t('addDueDiligenceExemption')
    },

    buttonText () {
      return this.editing ? this.$t('saveChanges') : this.$t('create')
    },

    options () {
      const options = []
      if (dueDiligenceApprovalEnabled()) {
        options.push({ model: 'requiresApproval', label: this.$t('requireSustainabilityApproval'), info: this.$t('requireSustainabilityApprovalInfo') })
      }
      if (speciesLayerEnabled()) {
        options.push({ model: 'autoAssignWhenNoHits', label: this.$t('autoAssignWhenNoHitsLabel'), info: this.$t('autoAssignWhenNoHitsInfo') })
      }
      return options
    },

    displacedAutoAssign () {
      if (!this.dueDiligenceExemption.autoAssignWhenNoHits) return null
      return this.allDueDiligenceExemptions.find(exemption =>
        exemption.autoAssignWhenNoHits &&
        exemption.dueDiligenceExemptionId !== this.dueDiligenceExemption.dueDiligenceExemptionId) ?? null
    },

    notes () {
      const notes = []
      if (!speciesLayerEnabled()) return notes
      if (this.displacedAutoAssign) notes.push(this.$t('autoAssignWhenNoHitsTakeoverInfo', { name: this.displacedAutoAssign.name }))
      if (this.dueDiligenceExemption.autoAssignWhenNoHits && this.dueDiligenceExemption.requiresApproval && dueDiligenceApprovalEnabled()) {
        notes.push(this.$t('autoAssignWithApprovalHint'))
      }
      return notes
    },

    canSubmit () {
      const name = this.dueDiligenceExemption.name.trim()
      return name.length > 0 && name.length <= this.maxNameLength
    }
  },

  data: () => ({
    dueDiligenceExemption: {
      name: '',
      description: '',
      requiresApproval: false,
      autoAssignWhenNoHits: false
    },
    maxNameLength: StringConstants.LongStringLength,
    maxDescriptionLength: StringConstants.ExtraExtraLongStringLength,
    rules: fieldRules.rules
  }),

  methods: {
    ...mapActions('due-diligence-exemption', ['createDueDiligenceExemption', 'updateDueDiligenceExemption']),

    async submitClicked () {
      if (!this.canSubmit) {
        this.setSnackError(this.$t('nameIsRequired'))
        return
      }

      const requestObj = cloneDeep(this.dueDiligenceExemption)
      requestObj.name = requestObj.name.trim()
      requestObj.description = (requestObj.description ?? '').trim() || null

      const displaced = this.displacedAutoAssign

      if (this.editing) {
        await this.updateDueDiligenceExemption(requestObj)
      } else {
        await this.createDueDiligenceExemption(requestObj)
      }

      this.setSnack(displaced
        ? this.$t('autoAssignExemptionMoved', { previous: displaced.name, current: requestObj.name })
        : this.$t(this.editing ? 'dueDiligenceExemptionUpdated' : 'dueDiligenceExemptionCreated'))
      this.$emit('due-diligence-exemption-mutated')
    }
  }
}
</script>

<style scoped>
/* Vuetify pins the alert icon with align-self: flex-start, which reads as off-center beside caption text */
.exemption-notes ::v-deep .v-alert__icon {
  align-self: center;
}
</style>
